import { useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem } from 'lucide-react'
import { useSettingsModal } from '../../components/settings/settings-modal/SettingsModalProvider'
import { createNadDebug } from '../../utils/debugLog'
import { formatCredits } from './billingTypes'

const log = createNadDebug('dw:billing-header')

// The header supplies its already-read wallet figure. Opening the card makes
// no second balance read, and unavailable/flag-hidden figures render no slot.
export default function CreditBalancePill({ balance, label }: { balance: number; label: string }) {
  const { t, i18n } = useTranslation('settings')
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { open: openSettings } = useSettingsModal()
  const [openedOn, setOpenedOn] = useState<string | null>(null)
  const open = openedOn === pathname
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const id = useId()
  const credits = formatCredits(balance, i18n.language)
  useEffect(() => {
    if (!open) return
    const outside = (event: MouseEvent) => { if (event.target instanceof Node && !root.current?.contains(event.target)) setOpenedOn(null) }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])
  const go = (add: boolean) => {
    log({ event: add ? 'credits-pill:add' : 'credits-pill:usage', trace_id: 'billing:header' })
    setOpenedOn(null)
    openSettings('usage-credits')
    navigate({ pathname: add ? '/settings/billing/add-credits' : '/settings/billing', search })
  }
  return <div ref={root} className="relative shrink-0" onBlur={event => { if (event.relatedTarget instanceof Node && !root.current?.contains(event.relatedTarget)) setOpenedOn(null) }} onKeyDown={event => {
    if (event.key === 'Escape' && open && !event.nativeEvent.isComposing) { event.preventDefault(); event.stopPropagation(); setOpenedOn(null); trigger.current?.focus() }
  }}>
    <button ref={trigger} type="button" data-testid="header-credit-pill" aria-expanded={open} aria-controls={open ? id : undefined} aria-haspopup="dialog" onClick={() => {
      log({ event: 'credits-pill:toggle', trace_id: 'billing:header', expanded: !open })
      setOpenedOn(open ? null : pathname)
    }} className="inline-flex h-8 items-center gap-2 rounded-full border border-input-border bg-surface-2 px-3 text-base text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <Gem className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
      <span className="whitespace-nowrap">{t('settingsModal.billing.packLabel', { credits })}</span>
    </button>
    {open && <div id={id} role="dialog" aria-modal="false" aria-label={label} data-testid="header-credit-popover" className="absolute right-0 z-50 mt-2 w-72 max-sm:fixed max-sm:inset-x-4 max-sm:top-14 max-sm:w-auto max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-surface-2 p-4 shadow-xl">
      <p className="text-base text-muted-foreground">{label}</p>
      <p className="flex items-center gap-2 py-3 text-xl font-semibold text-foreground"><Gem className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" />{credits}</p>
      <div className="flex gap-2">
        <button type="button" onClick={() => go(false)} className="h-9 flex-1 rounded-lg border border-input-border bg-surface-1 px-3 text-base text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t('settingsModal.billing.viewUsage')}</button>
        <button type="button" onClick={() => go(true)} className="h-9 flex-1 rounded-lg bg-foreground px-3 text-base font-medium text-background hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t('settingsModal.billing.addCredits')}</button>
      </div>
    </div>}
  </div>
}
